import tippingByIso2 from '../data/countryInfo/tipping.json';
import electricalByName from '../data/countryInfo/electrical.json';
import visaRows from '../data/countryInfo/visa_us_ca.json';

export interface TippingInfo {
  country: string;
  restaurants: string;
  tours: string;
}

export interface ElectricalInfo {
  country: string;
  plugTypes: string[];
  voltage: string;
  frequency: string;
}

export interface VisaInfo {
  citizenCountry: string;
  destinationISO2: string;
  destinationCountry: string;
  visaRequired: boolean;
  visaDuration: string | null;
}

const tippingMap = tippingByIso2 as Record<string, TippingInfo>;
const electricalMap = electricalByName as Record<string, ElectricalInfo>;

export function tippingInfoForIso2(iso2: string | null | undefined): TippingInfo | null {
  if (!iso2?.trim()) return null;
  return tippingMap[iso2.trim().toUpperCase()] ?? null;
}

const SOVEREIGN_ALIASES: Record<string, string> = {
  scotland: 'United Kingdom',
  wales: 'United Kingdom',
  england: 'United Kingdom',
  'northern ireland': 'United Kingdom',
  uk: 'United Kingdom',
  usa: 'United States',
  us: 'United States',
  uae: 'United Arab Emirates',
  türkiye: 'Türkiye',
  turkey: 'Türkiye',
};

export function normalizeCountryName(name: string): string {
  const trimmed = name.trim();
  const alias = SOVEREIGN_ALIASES[trimmed.toLowerCase()];
  return alias ?? trimmed;
}

const GEOCODE_NAME_TO_ISO2: Record<string, string> = {
  'united kingdom of great britain and northern ireland (the)': 'GB',
  'united kingdom of great britain and northern ireland': 'GB',
  'great britain': 'GB',
  'isle of man': 'IM',
};

const iso2ByCountryName: Record<string, string> = (() => {
  const map: Record<string, string> = { ...GEOCODE_NAME_TO_ISO2 };
  for (const [iso2, info] of Object.entries(tippingMap)) {
    const key = info.country.trim().toLowerCase();
    if (key) map[key] = iso2.toUpperCase();
  }
  for (const [alias, canonical] of Object.entries(SOVEREIGN_ALIASES)) {
    const iso2 = map[canonical.toLowerCase()];
    if (iso2) map[alias] = iso2;
  }
  return map;
})();

/** Resolve ISO2 from a country name (or a 2-letter code). */
export function iso2FromCountryName(name: string | null | undefined): string | null {
  if (!name?.trim()) return null;
  const raw = name.trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const normalized = normalizeCountryName(raw).toLowerCase();
  return iso2ByCountryName[normalized] ?? iso2ByCountryName[raw.toLowerCase()] ?? null;
}

export function electricalInfoForCountry(country: string | null | undefined): ElectricalInfo | null {
  if (!country?.trim()) return null;
  const normalized = normalizeCountryName(country);
  if (electricalMap[normalized]) return electricalMap[normalized];
  const lower = normalized.toLowerCase();
  const exact = Object.entries(electricalMap).find(([k]) => k.toLowerCase() === lower);
  if (exact) return exact[1];
  const partial = Object.entries(electricalMap).find(
    ([k]) => lower.includes(k.toLowerCase()) || k.toLowerCase().includes(lower)
  );
  return partial?.[1] ?? null;
}

export function plugTypeDescription(type: string): string {
  switch (type.toUpperCase()) {
    case 'A':
      return '2 flat parallel pins (US)';
    case 'B':
      return '2 flat pins + ground (US)';
    case 'C':
      return '2 round pins (Europe)';
    case 'D':
      return '3 large round pins (India)';
    case 'E':
      return '2 round pins + hole (France)';
    case 'F':
      return '2 round pins + clips (Germany/Schuko)';
    case 'G':
      return '3 rectangular pins (UK)';
    case 'H':
      return '3 pins (Israel)';
    case 'I':
      return '3 flat pins (Australia)';
    case 'J':
      return '3 round pins (Switzerland)';
    case 'K':
      return '3 pins (Denmark)';
    case 'L':
      return '3 round inline pins (Italy)';
    case 'M':
      return '3 large round pins (South Africa)';
    case 'N':
      return '3 round pins (Brazil)';
    default:
      return type;
  }
}

/** True when destination plugs are not US A/B-only (adapter likely needed). */
export function needsAdapterWarning(plugTypes: string[]): boolean {
  if (plugTypes.length === 0) return false;
  return !plugTypes.every((t) => t === 'A' || t === 'B');
}

export function visaInfoFor(
  citizenIso2: string | null | undefined,
  destinationIso2: string | null | undefined
): VisaInfo | null {
  if (!citizenIso2?.trim() || !destinationIso2?.trim()) return null;
  const citizen = citizenIso2.trim().toUpperCase();
  const dest = destinationIso2.trim().toUpperCase();
  const row = (visaRows as Array<Record<string, string>>).find(
    (r) =>
      String(r.citizen_country).toUpperCase() === citizen &&
      String(r.ISO2_country_code).toUpperCase() === dest
  );
  if (!row) return null;
  const requiredRaw = String(row.visa_required || '').trim().toLowerCase();
  return {
    citizenCountry: citizen,
    destinationISO2: dest,
    destinationCountry: String(row.country_name || dest),
    visaRequired: requiredRaw === 'yes' || requiredRaw === 'true' || requiredRaw === '1',
    visaDuration: row.visa_duration?.trim() ? row.visa_duration.trim() : null,
  };
}

/** Flag emoji from ISO2 (regional indicator symbols). */
export function flagEmojiFromIso2(iso2: string | null | undefined): string {
  if (!iso2 || iso2.length !== 2) return '🏳️';
  const upper = iso2.toUpperCase();
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (upper.charCodeAt(0) - 65), A + (upper.charCodeAt(1) - 65));
}
